import React, { useState, useEffect } from 'react';
import { Link, Outlet, NavLink } from 'react-router-dom';
import {
  FiHome,
  FiSearch,
  FiClock,
  FiUsers,
  FiSettings,
  FiMenu,
  FiX,
  FiCreditCard,
  FiActivity
} from 'react-icons/fi';
import { authService } from '../../services/auth';
import { getBalance, getTenantById } from '../../services/api';
import ThemeToggle from '../../components/ThemeToggle';
import UserMenu from '../../components/UserMenu';
import './Dashboard.css';

const Dashboard = () => {
  const user = authService.getUser();
  const [saldo, setSaldo] = useState<string>('0.00');
  const [precoConsulta, setPrecoConsulta] = useState<string>('0.00');
  const [precoOcr, setPrecoOcr] = useState<string>('0.00');
  const [precoPidKey, setPrecoPidKey] = useState<string>('0.00');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    if (user && user.role === 'client') {
      carregarDadosFinanceiros();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarDadosFinanceiros = async () => {
    try {
      if (user && user.tenantId) {
        const tenantResponse = await getTenantById(user.tenantId);

        if (tenantResponse.success && tenantResponse.tenant) {
          setSaldo(tenantResponse.tenant.credits?.toString() || '0.00');

          const apiResponse = await getBalance();
          if (apiResponse.success && apiResponse.data) {
            const apiPrecoConsulta = apiResponse.data.price || '1.00';
            const apiPrecoOcr = apiResponse.data.ocrprice || '0.50';
            const apiPrecoPidKey = apiResponse.data.pidkeyprice || '0.25';

            setPrecoConsulta(apiPrecoConsulta);
            setPrecoOcr(apiPrecoOcr);
            setPrecoPidKey(apiPrecoPidKey);

            if (tenantResponse.tenant.prices && tenantResponse.tenant.prices.length > 0) {
              const getCidPrice = tenantResponse.tenant.prices.find(
                (p: any) => p.queryType && p.queryType.name === 'GetCID'
              );
              if (getCidPrice) setPrecoConsulta(getCidPrice.price.toString());

              const ocrPrice = tenantResponse.tenant.prices.find(
                (p: any) => p.queryType && p.queryType.name === 'OCR'
              );
              if (ocrPrice) setPrecoOcr(ocrPrice.price.toString());

              const pidKeyPrice = tenantResponse.tenant.prices.find(
                (p: any) => p.queryType && p.queryType.name === 'PIDKey'
              );
              if (pidKeyPrice) {
                setPrecoPidKey(pidKeyPrice.price.toString());
              } else if (tenantResponse.tenant.email === 'teste@teste.com') {
                setPrecoPidKey('0.25');
              }
            } else if (tenantResponse.tenant.email === 'teste@teste.com') {
              setPrecoPidKey('0.25');
            }
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
    }
  };

  const isTenant = user && user.role === 'client';
  const isTenantAdmin = user?.isTenantAdmin || false;
  const roleLabel =
    user?.role === 'admin'
      ? 'Administrador'
      : isTenantAdmin
        ? 'Admin Cliente'
        : 'Cliente';

  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 1024) setSidebarOpen(false);
  };

  return (
    <div className={`dashboard ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-header">
          <img
            src="https://digitalsac.io/wp-content/uploads/2024/02/digitalsac2026.png"
            alt="DigitalSac"
            className="sidebar-logo"
          />
          <div className="sidebar-brand">
            <h2>GetCID</h2>
            <span>Console</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <span className="sidebar-section">Principal</span>
          <NavLink to="/dashboard" end onClick={closeSidebarOnMobile} className="nav-link">
            <FiHome /> <span>Início</span>
          </NavLink>
          <NavLink to="/dashboard/consultas" onClick={closeSidebarOnMobile} className="nav-link">
            <FiSearch /> <span>Consultas</span>
          </NavLink>
          <NavLink to="/dashboard/historico" onClick={closeSidebarOnMobile} className="nav-link">
            <FiClock /> <span>Histórico</span>
          </NavLink>

          {isTenant && isTenantAdmin && (
            <>
              <span className="sidebar-section">Gerenciamento</span>
              <NavLink to="/dashboard/profile" onClick={closeSidebarOnMobile} className="nav-link">
                <FiUsers /> <span>Usuários</span>
              </NavLink>
            </>
          )}

          {!isTenant && (
            <>
              <span className="sidebar-section">Administração</span>
              <NavLink to="/configuracoes" onClick={closeSidebarOnMobile} className="nav-link">
                <FiSettings /> <span>Configurações</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <UserMenu profileBase="/dashboard" roleLabel={roleLabel} />
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      <main className="main-content">
        <header className="dashboard-header">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((s) => !s)}
            aria-label="Abrir menu"
          >
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>

          <div className="header-title">
            <h1>Dashboard</h1>
            <p>Bem-vindo(a), <strong>{user?.name || 'Usuário'}</strong></p>
          </div>

          <div className="header-actions">
            {isTenant && (
              <div className="header-balance">
                <FiCreditCard />
                <div>
                  <span className="header-balance-label">Saldo</span>
                  <span className="header-balance-value">{parseFloat(saldo).toFixed(2)}</span>
                </div>
              </div>
            )}
            <ThemeToggle />
          </div>
        </header>

        <Outlet />

        <div className="dashboard-content">
          {isTenant && (
            <div className="tenant-dashboard">
              <div className="stats-grid">
                <div className="stat-card stat-card-primary">
                  <div className="stat-icon"><FiCreditCard /></div>
                  <div className="stat-body">
                    <span className="stat-label">Saldo atual</span>
                    <span className="stat-value">{parseFloat(saldo).toFixed(2)}</span>
                    <span className="stat-hint">Créditos descontados automaticamente a cada consulta</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon"><FiSearch /></div>
                  <div className="stat-body">
                    <span className="stat-label">Consulta GetCID</span>
                    <span className="stat-value">{precoConsulta}</span>
                    <span className="stat-hint">créditos por consulta</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon"><FiActivity /></div>
                  <div className="stat-body">
                    <span className="stat-label">Consulta OCR</span>
                    <span className="stat-value">{precoOcr}</span>
                    <span className="stat-hint">créditos por consulta</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon"><FiSettings /></div>
                  <div className="stat-body">
                    <span className="stat-label">Verificação PidKey</span>
                    <span className="stat-value">{precoPidKey}</span>
                    <span className="stat-hint">créditos por consulta</span>
                  </div>
                </div>
              </div>

              <div className="card cta-card">
                <div>
                  <h3>Ir para Consultas</h3>
                  <p>Realize consultas de CID, PidKey e OCR em poucos cliques.</p>
                </div>
                <Link to="/dashboard/consultas" className="card-link">
                  Abrir consultas
                </Link>
              </div>
            </div>
          )}

          {!isTenant && (
            <div className="admin-overview">
              <div className="card welcome-card">
                <h3>Visão Geral</h3>
                <p>Bem-vindo ao sistema de gestão GetCID. Use o menu lateral para navegar entre as funcionalidades.</p>
                <div className="admin-info">
                  <strong>Nota:</strong> como administrador, suas consultas não descontam créditos do sistema.
                </div>
              </div>

              <div className="card cta-card">
                <div>
                  <h3>Consultas Rápidas</h3>
                  <p>Acesse as ferramentas de consulta diretamente.</p>
                </div>
                <Link to="/dashboard/consultas" className="card-link">
                  Ir para consultas
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
